import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import { cn } from "./cn";

export const comboboxInputClasses =
    "h-9 w-full rounded-md border border-border bg-surface px-3 text-sm text-fg " +
    "placeholder:text-muted transition-colors " +
    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

const popupClasses =
    "max-h-72 w-[var(--anchor-width)] overflow-x-hidden overflow-y-auto rounded-md border border-border " +
    "bg-surface p-1 shadow-lg outline-none " +
    "transition-opacity duration-100 " +
    "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0";

const itemClasses =
    "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none " +
    "data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg " +
    "data-[selected]:font-medium";

/**
 * Searchable combobox (Base UI) — the design-system primitive behind the
 * Typeahead. Drives input-as-you-type filtering over large lists. See
 * components/Typeahead.tsx for a fully composed example.
 */
export const Combobox = {
    Root: BaseCombobox.Root,
    Trigger: BaseCombobox.Trigger,
    Icon: BaseCombobox.Icon,
    Clear: BaseCombobox.Clear,
    List: BaseCombobox.List,
    Value: BaseCombobox.Value,
    Input: function Input({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseCombobox.Input>) {
        return (
            <BaseCombobox.Input
                className={cn(comboboxInputClasses, className as string)}
                {...props}
            />
        );
    },
    Empty: function Empty({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseCombobox.Empty>) {
        return (
            <BaseCombobox.Empty
                className={cn("px-2 py-2 text-sm text-muted", className as string)}
                {...props}
            />
        );
    },
    Item: function Item({
        className,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseCombobox.Item>) {
        return (
            <BaseCombobox.Item className={cn(itemClasses, className as string)} {...props} />
        );
    },
    /** Portal + positioner + popup in one convenient wrapper. */
    Content: function Content({
        className,
        children,
        sideOffset = 4,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseCombobox.Positioner>) {
        return (
            <BaseCombobox.Portal>
                {/* z-index must live on the Positioner — it is the positioned
                    (absolute) element; the Popup is static, so z-index there is
                    ignored and a sticky table header would paint over it. */}
                <BaseCombobox.Positioner className="z-[1100]" sideOffset={sideOffset} {...props}>
                    <BaseCombobox.Popup className={cn(popupClasses, className as string)}>
                        {children}
                    </BaseCombobox.Popup>
                </BaseCombobox.Positioner>
            </BaseCombobox.Portal>
        );
    },
};
